/**
 * Sentry error tracking configuration for web app.
 * Initialize this BEFORE rendering the app.
 *
 * Required env var: VITE_SENTRY_DSN
 *
 * Usage:
 *   import { initSentry } from './lib/sentry';
 *   initSentry();
 */
import * as Sentry from '@sentry/react';
import {
  createSentryHelpers,
  createBeforeSendFilter,
  createBeforeBreadcrumbFilter,
} from '@pantry/shared/lib';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

/**
 * Build the release string for Sentry.
 * Format: "1.2.0+a3f2c1b" (semver + short git SHA)
 * Falls back to just version or 'local' if no version info available.
 */
function buildReleaseString(): string {
  const version = import.meta.env.VITE_APP_VERSION;
  const commitSha = import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA;

  if (version && commitSha) {
    return `${version}+${commitSha.slice(0, 7)}`;
  }
  if (version) {
    return version;
  }
  if (commitSha) {
    return commitSha.slice(0, 7);
  }
  return 'local';
}

export function initSentry(): void {
  // Skip Sentry in development or if no DSN provided
  if (!SENTRY_DSN) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Skipped - no VITE_SENTRY_DSN configured');
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: buildReleaseString(),

    // Performance monitoring - sample 10% of transactions in production
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay - capture 1% of sessions, 100% of sessions with errors
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    beforeSend: createBeforeSendFilter(),
    beforeBreadcrumb: createBeforeBreadcrumbFilter(),
  });
}

// Create and export helpers using shared factory
export const { captureException, captureMessage, setUser, addBreadcrumb } = createSentryHelpers(
  Sentry,
  !!SENTRY_DSN
);

// Re-export ErrorBoundary for use in components
export { ErrorBoundary } from '@sentry/react';
